<script lang="ts">
  import { onMount } from 'svelte'
  import { locale, tStore } from '../i18n'
  import { cpvLabel } from '../composables/cpvLabel'
  import type { CpvCode } from '../api/cpvCodes'
  import { loadCpvCodes } from '../stores/cpvCodes'

  interface Props {
    selected: string[]
  }

  let { selected = $bindable() }: Props = $props()

  let catalog = $state<CpvCode[]>([])
  let loading = $state(true)
  let errorMsg = $state<string | null>(null)
  let query = $state('')
  let showSelectedOnly = $state(false)
  // Empty = every division collapsed by default. With the full ~9,450-code
  // catalog, rendering every leaf row up front (the old "expanded unless
  // collapsed" default, fine at ~109 codes) would put ~9,450 checkboxes in
  // the DOM at once. Groups the user has explicitly opened stay open even
  // if they later clear the search — see `collapsed` below, which only
  // auto-opens filtered-to groups, never auto-closes a manual choice.
  let expandedGroups = $state<Set<string>>(new Set())

  onMount(() => {
    void load()
  })

  // "Selected only" with nothing selected would just show an empty list —
  // reset it the moment the last selection is removed (e.g. via chip
  // removal or Clear all), not just block re-checking it.
  $effect(() => {
    if (selected.length === 0) showSelectedOnly = false
  })

  async function load() {
    loading = true
    errorMsg = null
    try {
      catalog = await loadCpvCodes()
    } catch (err) {
      errorMsg = err instanceof Error ? err.message : String(err)
    } finally {
      loading = false
    }
  }

  const selectedSet = $derived(new Set(selected))

  const isFiltering = $derived(query.trim() !== '' || showSelectedOnly)

  const filtered = $derived.by(() => {
    const q = query.trim().toLowerCase()
    let rows = catalog
    if (q) {
      rows = rows.filter(
        (c) => c.code.startsWith(q) || cpvLabel(c, $locale).toLowerCase().includes(q),
      )
    }
    if (showSelectedOnly) {
      rows = rows.filter((c) => selectedSet.has(c.code))
    }
    return rows
  })

  interface Group {
    key: string
    rows: CpvCode[]
  }

  // Grouped by CPV division (the code's first 2 digits) so a large catalog
  // stays browsable instead of one long flat list.
  const groups = $derived.by((): Group[] => {
    const byKey = new Map<string, CpvCode[]>()
    for (const row of filtered) {
      const key = row.code.slice(0, 2)
      const arr = byKey.get(key)
      if (arr) arr.push(row)
      else byKey.set(key, [row])
    }
    return [...byKey.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, rows]) => ({ key, rows }))
  })

  function toggleCode(code: string) {
    selected = selectedSet.has(code) ? selected.filter((c) => c !== code) : [...selected, code]
  }

  function groupState(rows: CpvCode[]): 'all' | 'some' | 'none' {
    const count = rows.filter((r) => selectedSet.has(r.code)).length
    if (count === 0) return 'none'
    return count === rows.length ? 'all' : 'some'
  }

  function toggleGroup(rows: CpvCode[]) {
    if (groupState(rows) === 'all') {
      const codes = new Set(rows.map((r) => r.code))
      selected = selected.filter((c) => !codes.has(c))
    } else {
      const toAdd = rows.filter((r) => !selectedSet.has(r.code)).map((r) => r.code)
      selected = [...selected, ...toAdd]
    }
  }

  function toggleCollapsed(key: string) {
    const next = new Set(expandedGroups)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    expandedGroups = next
  }

  function clearAll() {
    selected = []
  }

  // Sets the native `indeterminate` DOM property, which has no declarative
  // Svelte binding — a group's "all selected under this division" checkbox
  // needs the third, partial-selection visual state.
  function indeterminate(node: HTMLInputElement, value: boolean) {
    node.indeterminate = value
    return {
      update(next: boolean) {
        node.indeterminate = next
      },
    }
  }
</script>

<div class="picker">
  <div class="picker__toolbar">
    <input
      class="input picker__search"
      type="text"
      placeholder={$tStore('components.cpvPicker.searchPlaceholder')}
      bind:value={query}
    />
    <label class="picker__toggle" class:picker__toggle--disabled={selected.length === 0}>
      <input type="checkbox" bind:checked={showSelectedOnly} disabled={selected.length === 0} />
      {$tStore('components.cpvPicker.selectedOnly')}
    </label>
    <button
      type="button"
      class="btn btn--sm btn--secondary"
      disabled={selected.length === 0}
      onclick={clearAll}
    >
      {$tStore('components.cpvPicker.clearAll')}
    </button>
  </div>

  <p class="picker__count">
    {$tStore('components.cpvPicker.selectedCount', { count: selected.length })}
  </p>

  {#if loading}
    <p class="muted">{$tStore('common.loading')}</p>
  {:else if errorMsg}
    <p class="error">{errorMsg}</p>
  {:else if groups.length === 0}
    <p class="muted">{$tStore('components.cpvPicker.noResults')}</p>
  {:else}
    <div class="picker__tree" role="tree">
      {#each groups as group (group.key)}
        {@const state = groupState(group.rows)}
        {@const collapsed = !isFiltering && !expandedGroups.has(group.key)}
        <div class="picker__group">
          <div class="picker__group-header">
            <button
              type="button"
              class="picker__collapse"
              aria-expanded={!collapsed}
              onclick={() => toggleCollapsed(group.key)}
            >
              <span class:picker__collapse-icon--open={!collapsed} class="picker__collapse-icon"
                >▸</span
              >
            </button>
            <label class="picker__group-label">
              <input
                type="checkbox"
                checked={state === 'all'}
                use:indeterminate={state === 'some'}
                onchange={() => toggleGroup(group.rows)}
              />
              <span class="picker__group-code">{group.key}000000</span>
              <span class="picker__group-count">({group.rows.length})</span>
            </label>
          </div>
          {#if !collapsed}
            <ul class="picker__rows">
              {#each group.rows as row (row.code)}
                <li>
                  <label class="picker__row">
                    <input
                      type="checkbox"
                      checked={selectedSet.has(row.code)}
                      onchange={() => toggleCode(row.code)}
                    />
                    <span class="picker__row-code">{row.code}</span>
                    <span class="picker__row-label">{cpvLabel(row, $locale)}</span>
                  </label>
                </li>
              {/each}
            </ul>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</div>

<style lang="scss">
  @use '../assets/styles/variables' as v;

  .picker {
    display: flex;
    flex-direction: column;
    gap: v.$space-sm;
    border: 1px solid v.$color-border;
    border-radius: v.$radius-md;
    padding: v.$space-md;
    background: v.$color-surface;
  }

  .picker__toolbar {
    display: flex;
    align-items: center;
    gap: v.$space-sm;
    flex-wrap: wrap;
  }

  .picker__search {
    flex: 1;
    min-width: 10rem;
  }

  .picker__toggle {
    display: flex;
    align-items: center;
    gap: v.$space-xs;
    font-size: 0.8125rem;
    color: v.$color-text-muted;
    white-space: nowrap;

    &--disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .picker__count {
    margin: 0;
    font-size: 0.75rem;
    color: v.$color-text-subtle;
  }

  .picker__tree {
    max-height: 22rem;
    overflow-y: auto;
    border-top: 1px solid v.$color-border;
    padding-top: v.$space-xs;
  }

  .picker__group {
    border-bottom: 1px solid v.$color-border;

    &:last-child {
      border-bottom: none;
    }
  }

  .picker__group-header {
    display: flex;
    align-items: center;
    gap: v.$space-xs;
    padding: v.$space-xs 0;
  }

  .picker__collapse {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.2rem;
    color: v.$color-text-muted;
    display: inline-flex;
  }

  .picker__collapse-icon {
    display: inline-block;
    transition: transform 0.15s ease;

    &--open {
      transform: rotate(90deg);
    }
  }

  .picker__group-label {
    display: flex;
    align-items: center;
    gap: v.$space-xs;
    cursor: pointer;
    font-weight: 600;
    font-size: 0.8125rem;
  }

  .picker__group-code {
    font-family: v.$font-mono;
  }

  .picker__group-count {
    color: v.$color-text-subtle;
    font-weight: 400;
  }

  .picker__rows {
    list-style: none;
    margin: 0;
    padding: 0 0 v.$space-xs v.$space-lg;
    display: flex;
    flex-direction: column;
  }

  .picker__row {
    display: flex;
    align-items: baseline;
    gap: v.$space-sm;
    padding: 0.2rem 0;
    cursor: pointer;
    font-size: 0.8125rem;
  }

  .picker__row-code {
    font-family: v.$font-mono;
    min-width: 5.5rem;
    flex-shrink: 0;
    color: v.$color-text;
  }

  .picker__row-label {
    color: v.$color-text-muted;
  }
</style>
